import { zodResolver } from '@hookform/resolvers/zod';
import { changePasswordRequest, z } from '@zenarty/shared';
import { KeyRound } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { api } from '../api';
import { useAuth } from '../auth';
import { Button, Callout, Card, Field, PageHeader } from '../components/kit';
import { useAction } from '../lib/queries';

const schema = changePasswordRequest
  .extend({ confirm: z.string() })
  .refine((v) => v.confirm === v.newPassword, {
    path: ['confirm'],
    message: 'Passwords do not match',
  });

export function ChangePasswordPage() {
  const { me, reloadMe } = useAuth();
  const form = useForm<z.input<typeof schema>, unknown, z.output<typeof schema>>({
    resolver: zodResolver(schema),
  });
  const save = useAction(
    ({ currentPassword, newPassword }: z.output<typeof schema>) =>
      api('/auth/password', { method: 'POST', body: { currentPassword, newPassword } }),
    {
      invalidate: [],
      success: 'Password changed. Your other sessions were signed out.',
      onSuccess: () => {
        form.reset();
        void reloadMe();
      },
    },
  );
  const e = form.formState.errors;
  return (
    <div className="stack" style={{ maxWidth: 560 }}>
      <PageHeader
        title="Change password"
        description="Use a long passphrase you don't use anywhere else. Signing in also needs your authenticator code."
      />
      {me?.passwordChangeRequired ? (
        <Callout tone="warn" title="Choose your own password to continue">
          You signed in with a one-time password. The rest of the app unlocks once you set your own.
        </Callout>
      ) : null}
      <Card>
        <form
          className="stack"
          onSubmit={(ev) => void form.handleSubmit((v) => save.mutate(v))(ev)}
          noValidate
        >
          <Field label="Current password" error={e.currentPassword?.message}>
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              {...form.register('currentPassword')}
            />
          </Field>
          <Field
            label="New password"
            hint="At least 12 characters. A short sentence works well."
            error={e.newPassword?.message}
          >
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              {...form.register('newPassword')}
            />
          </Field>
          <Field label="Confirm new password" error={e.confirm?.message}>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              {...form.register('confirm')}
            />
          </Field>
          <div>
            <Button type="submit" variant="primary" icon={KeyRound} loading={save.isPending}>
              Change password
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
